import _styled from '@emotion/styled/base';
import memoize from 'fast-memoize';
import lru from 'tiny-lru';
import { css } from './css.js';
import { warn, hasFunctionStyles, shouldForwardProp } from './utils.js';
import 'deepmerge';
import 'rtl-css-js';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

let themeCount = 1;
const themeMap = new WeakMap();
const sxCache = new lru();

const createStyleSerializer = cacheProps => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const fn = _ref => {
    let {
      theme,
      sx,
      children,
      ...props
    } = _ref;

    if (process.env.NODE_ENV !== "production" && Object.keys(theme).length === 0) {
      warn("No theme found. Please wrap your components in an IndeedThemeProvider for proper operation. See https://experience.indeed.design/fundamentals/ids/getting-started for more information");
    }

    let themeId = themeMap.get(theme);

    if (themeId === undefined) {
      themeId = themeCount;
      themeCount = themeCount + 1;

      if (themeCount > 50) {
        warn(`There are ${themeCount} themes in the theme cache. You may experience performance issues due to the large cache size. Please ensure that you are providing the same theme object (in memory) to IndeedThemeProvider. See https://experience.indeed.design/fundamentals/ids/ifl-performance-guidelines for more information.`);
      }

      themeMap.set(theme, themeId);
    }

    const propsKey = (cacheProps ? cacheProps.map(k => {
      var _props$k;

      return (_props$k = props[k]) != null ? _props$k : "0";
    }).join("-") : Object.values(props).join("-")) || "0";
    return `${propsKey}-${themeId}`;
  };

  return fn;
};

const createSxSerializer = () => {
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const fn = args => {
    const [input, theme] = args;

    if (process.env.NODE_ENV !== "production" && Object.keys(theme).length === 0) {
      warn("No theme found. Please wrap your components in an IndeedThemeProvider for proper operation. See https://experience.indeed.design/fundamentals/ids/getting-started for more information");
    }

    let themeId = themeMap.get(theme);

    if (themeId === undefined) {
      themeId = themeCount;
      themeCount = themeCount + 1;

      if (themeCount > 50) {
        warn(`There are ${themeCount} themes in the theme cache. You may experience performance issues due to the large cache size. Please ensure that you are providing the same theme object (in memory) to IndeedThemeProvider. See https://experience.indeed.design/fundamentals/ids/ifl-performance-guidelines for more information.`);
      }

      themeMap.set(theme, themeId);
    }

    return `${JSON.stringify(input)}-${themeId}`;
  };

  return fn;
};

const memoizedCss = memoize((input, theme) => css(input)(theme), {
  serializer: createSxSerializer(),
  cache: {
    create() {
      return sxCache;
    }

  }
});

const sxProp = props => {
  const {
    sx,
    theme
  } = props;

  if (sx) {
    if (!hasFunctionStyles(sx)) {
      return memoizedCss(sx, theme);
    }

    return css(sx)(theme);
  }

  return null;
};

const themed = (tag, options) => {
  const {
    styleProps,
    ...styledOptions
  } = options != null ? options : {};
  let optionsShouldForwardProp;

  if (styledOptions) {
    optionsShouldForwardProp = styledOptions.shouldForwardProp;
  }

  const shouldForwardProp$1 = optionsShouldForwardProp ? prop => // shouldForwardProp is composed of option shouldForwardProp & default shouldForwardProp
  optionsShouldForwardProp(prop) && shouldForwardProp(prop) : shouldForwardProp;
  styledOptions.shouldForwardProp = shouldForwardProp$1;

  const factory = /*#__PURE__*/_styled(tag, process.env.NODE_ENV === "production" ? _extends({}, {
    target: "eac13zx0"
  }, styledOptions) : _extends({}, {
    target: "eac13zx0",
    label: "factory"
  }, styledOptions));

  return function () {
    for (var _len = arguments.length, styleObjects = new Array(_len), _key = 0; _key < _len; _key++) {
      styleObjects[_key] = arguments[_key];
    }

    const cacheProps = styleProps; // if this is a 'simple' themed component, cache key is always the same

    if (styleObjects.length === 1 && typeof styleObjects[0] === "object" || cacheProps === false) {
      return factory(...styleObjects, sxProp);
    }

    const memoizedEmotionStyles = memoize(props => {
      return styleObjects.map(objOrFn => typeof objOrFn === "function" ? css(objOrFn(props))(props.theme) : objOrFn);
    }, {
      serializer: createStyleSerializer(cacheProps),
      cache: {
        create() {
          return lru(1000);
        }

      }
    });
    const c = factory(memoizedEmotionStyles, sxProp);
    c.__themedCacheProps = cacheProps; // eslint-disable-next-line @typescript-eslint/unbound-method

    c.__withComponent = c.withComponent; // IFL-1636: Ensure cacheProps are taken into account when using .withComponent
    // Override the default behavior of Emotion's withComponent when using a `themed`
    // component as `nextTag`.
    // See https://github.com/emotion-js/emotion/blob/314a5fb08b0f730e7aa88da0c974dfea13cc9b32/packages/styled/src/base.js#L202-L215

    c.withComponent = (nextTag, nextOptions) => {
      return nextTag.__themedCacheProps ? themed(nextTag, { ...options,
        ...nextOptions,
        styleProps: [...(cacheProps || []), ...(nextTag.__themedCacheProps || [])]
      })(...styleObjects) : c.__withComponent(nextTag, nextOptions);
    };

    return c;
  };
};

export { themed };
